import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { fetchAttachmentBlob } from '@/api/media';
import { ImageLightbox } from './ImageLightbox';
import type { MessageAttachment } from '@/types';

/**
 * WS-E.4 — compact attachment rendering shared by every staff message
 * bubble (MsgBubble, the legacy conversations/MessageBubble). Attachments
 * must NEVER dominate the chat column: images render as a thumbnail
 * hard-capped at ~140px (click for the full-size view); everything else
 * renders as a small filename+size chip (click to download). The
 * MediaAttachmentService content endpoint is landing on a parallel stream
 * and is NOT deployed yet, so a failed fetch is the DEFAULT path today —
 * every failure degrades to a plain chip with a non-blocking error
 * affordance. Never throws out of render, never blanks the bubble, never
 * leaves a spinner up forever.
 */

/** Human-readable file size, e.g. "3.2 KB" / "1.1 MB". No existing size
 * formatter lives in utils/formatters — this is the smallest local helper. */
function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${unitIndex === 0 ? value : value.toFixed(1)} ${units[unitIndex]}`;
}

function isImageAttachment(mimeType: string | undefined): boolean {
  return (mimeType ?? '').toLowerCase().startsWith('image/');
}

/** Triggers a browser download of `blob` under `fileName` without navigating. */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'attachment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  maxWidth: 180,
  padding: '4px 8px',
  borderRadius: 6,
  border: '1px solid var(--border, #e2e8f0)',
  background: 'var(--surface-2, #f8fafc)',
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'left',
};

/** Non-image chip: file icon + filename (ellipsis, ~180px cap) + size. Click downloads. */
const FileAttachmentChip: React.FC<{ attachment: MessageAttachment; loadFailed?: boolean }> = ({
  attachment,
  loadFailed = false,
}) => {
  const [downloadError, setDownloadError] = useState(false);
  // WS-E.R-B5 — the auto-clear timer must be cancelled on unmount, otherwise
  // a component unmounted inside the 4s window does a post-unmount setState.
  const errorTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current !== null) {
        window.clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    setDownloadError(false);
    fetchAttachmentBlob(attachment.fileId, attachment.plane)
      .then((blob) => downloadBlob(blob, attachment.fileName))
      .catch(() => {
        setDownloadError(true);
        errorTimeoutRef.current = window.setTimeout(() => {
          setDownloadError(false);
          errorTimeoutRef.current = null;
        }, 4000);
      });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="attachment-chip"
      title={attachment.fileName}
      style={chipStyle}
    >
      <Icon name="clip" size={14} />
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          fontSize: 12.5,
        }}
      >
        {attachment.fileName}
      </span>
      <span style={{ fontSize: 11, opacity: 0.65, flexShrink: 0 }}>
        {formatAttachmentSize(attachment.sizeBytes)}
      </span>
      {(loadFailed || downloadError) && (
        <span role="alert" data-testid="attachment-error" title="Download failed" style={{ fontSize: 11, color: 'var(--danger, #dc2626)' }}>
          !
        </span>
      )}
    </button>
  );
};

/** Image thumbnail, hard-capped at ~140px. Click opens the full-size view. */
const ImageAttachment: React.FC<{ attachment: MessageAttachment }> = ({ attachment }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setObjectUrl(null);

    fetchAttachmentBlob(attachment.fileId, attachment.plane)
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      // Revoke on unmount AND whenever the fileId changes (this effect
      // re-runs) — never leak the previous object URL. This is the INLINE
      // thumbnail's own tracked URL, kept exactly as-is; the full-size click
      // handler below deliberately does NOT share this URL (see WS-E.R-B4).
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [attachment.fileId]);

  // The endpoint isn't deployed yet, so this is the DEFAULT path today —
  // degrade to the plain chip rather than a permanently-loading thumbnail.
  if (failed || !objectUrl) {
    return <FileAttachmentChip attachment={attachment} loadFailed={failed} />;
  }

  // Click shows the picture larger IN THIS WINDOW (ImageLightbox) - never a new tab. The lightbox is rendered by this
  // component, so switching conversations (which unmounts it and revokes the URL) closes it too.
  const handleOpenFullSize = () => setLightbox(true);

  return (
    <>
      <button
        type="button"
        onClick={handleOpenFullSize}
        data-testid="attachment-thumbnail-button"
        aria-label={`Open full-size image: ${attachment.fileName}`}
        style={{
          padding: 0,
          border: 'none',
          background: 'none',
          lineHeight: 0,
          cursor: 'pointer',
          display: 'block',
        }}
      >
        <img
          src={objectUrl}
          alt={attachment.fileName}
          data-testid="attachment-thumbnail"
          style={{
            maxWidth: 140,
            maxHeight: 140,
            objectFit: 'cover',
            borderRadius: 8,
            display: 'block',
          }}
        />
      </button>
      {lightbox && <ImageLightbox url={objectUrl} fileName={attachment.fileName} onClose={() => setLightbox(false)} />}
    </>
  );
};

export interface MessageAttachmentsProps {
  attachments?: MessageAttachment[];
}

/** Renders every attachment on a message as a compact row below the text. */
export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div
      data-testid="message-attachments"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}
    >
      {attachments.map((a) =>
        isImageAttachment(a.mimeType) ? (
          <ImageAttachment key={a.fileId} attachment={a} />
        ) : (
          <FileAttachmentChip key={a.fileId} attachment={a} />
        ),
      )}
    </div>
  );
};
